/* eslint-disable no-case-declarations */
import type {
  CounterLog,
  MachineStatusLog,
  MaintenanceLog,
  MoveModuleLog,
  MutationEvent,
  MutationEventMaintenance,
  MutationEventWithLog,
  ResetOverdueLog,
  StockLog,
} from '@gmao/types';
import type { EventMergeRecord } from '../../../mutations-merger';
import * as Sentry from '@sentry/vue';
import { isAfter } from 'date-fns';
import { basename } from 'pathe';
import { getMissionId, missionContextState } from '../../../features';
import { client, thenData } from '../../management/api';
import { getUnsyncedEvents } from './clean';
import { downloaderAddFiles } from './files-downloader';
import { idb, missionConfig } from './idb';
import { socketIo } from './socket-io';
import { getLastMoveLog } from './utils';

// Per request: the client has none, and a slow photo upload must not cancel the whole sync
const SYNC_TIMEOUT = 30_000;
const UPLOAD_TIMEOUT = 120_000;
const CLOCK_OFFSET_KEY = 'clockOffset';

// Device clock minus server clock, as last measured when the server answered a sync (ms).
// shortcut: the latency of the request is ignored (some seconds at worst), and nothing is known
// before the first sync of this device
let clockOffset = Number(globalThis.localStorage?.getItem(CLOCK_OFFSET_KEY)) || 0;

export function getClockOffset() {
  return clockOffset;
}

// What time it is for the server: the device clock may be minutes off
export function serverNow() {
  return new Date(Date.now() - clockOffset);
}

function trackClock(serverTime: string) {
  clockOffset = Date.now() - new Date(serverTime).getTime();
  globalThis.localStorage?.setItem(CLOCK_OFFSET_KEY, String(clockOffset));
}

let syncing: Promise<void> | undefined;
let syncAgain = false;

// One run at a time (the server replays are harmless, but concurrent batches are not worth it):
// a trigger received meanwhile schedules a single extra run. Never rejects: a failed run is
// reported to Sentry and retried by the next trigger (mutation, socket `connect`, `online`).
export function syncLocalToMain(): Promise<void> {
  if (syncing) {
    syncAgain = true;
    return syncing;
  }

  syncing = (async () => {
    try {
      do {
        syncAgain = false;
        // Every request is limited in `sendLocalEvents`, so a hanging one (captive portal) cannot
        // keep the lock forever
        await sendLocalEvents();
      } while (syncAgain);
    }
    catch {
      // Not connected, no mission selected, or already reported to Sentry
    }
    finally {
      syncing = undefined;
    }
  })();

  return syncing;
}

async function sendLocalEvents() {
  if (!socketIo.connected)
    return;

  const missionId = getMissionId();

  if (await withTimeout(hasForeignUpdates(missionId), SYNC_TIMEOUT)) {
    missionContextState.hasEventsUpdates = true;
    return;
  }

  // A refused event is not sent again by itself: the merge screen asks the user what to do
  const events = (await getUnsyncedEvents(missionId)).filter(item => !item.rejection);
  if (!events.length)
    return;

  try {
    await uploadMissingFiles(events);
    // The abandoned request is safe to repeat: the server ignores the events it already has
    const { results, serverTime } = await withTimeout(
      client.operations.mutation.sync({ missionId, events }).then(thenData),
      SYNC_TIMEOUT,
    );
    trackClock(serverTime);

    const refusals = await handleRefusals(results);
    if (refusals) {
      missionContextState.hasEventsUpdates = true;
      throw new Error(refusals);
    }
  }
  catch (err) {
    Sentry.setContext('sync', syncContext(events));
    Sentry.captureException(err, {
      data: {
        events,
      },
    });
    throw err;
  }
}

// What tells a failure of the field from a failure of the code: how much waits, for how long, and
// whether the clock of the device can be trusted
function syncContext(events: MutationEvent[]) {
  const oldest = Math.min(...events.map(event => new Date(event.createdAt).getTime()));
  return {
    pending: events.length,
    oldestMinutes: Math.round((Date.now() - oldest) / 60_000),
    clockOffsetMinutes: Math.round(clockOffset / 60_000),
  };
}

// The server answers per event: one refusal does not stop the others, but must not go unnoticed.
// Refused events are marked on the device, and come back when they are synced (or deleted)
export async function handleRefusals(
  results: { id: string; status: string; code?: string; message?: string }[],
) {
  const refused = results.filter(item => item.status === 'rejected');
  if (!refused.length)
    return undefined;

  for (const { id, code, message } of refused)
    await idb.mutationsEvents.update(id, { rejection: { code, message } });

  return `${refused.length} event(s) refused by the server:\n${refused.map(item => `- ${item.message || item.code}`).join('\n')}`;
}

// A safety copy of what was recorded and not sent yet, to keep it when the device is wiped.
// shortcut: the attachments are not in it (blobs), they stay in the database of the device
export async function exportUnsyncedEvents(missionId?: string) {
  const events = await Promise.all(
    ((await getUnsyncedEvents(missionId)) as MutationEventWithLog[]).map(attachLog),
  );
  return JSON.stringify({ exportedAt: new Date().toISOString(), events }, null, 2);
}

// Resolves once no send is running: a page reload in the middle of one would cut the request
export function whenSyncIdle(): Promise<void> {
  return syncing ?? Promise.resolve();
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const error = new Error(`Sync timeout after ${ms}ms`);
      Sentry.captureException(error);
      reject(error);
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// Events of this device whose echo was lost count as "updates" on the server, and would block
// the sending forever: only events we do not know need a merge first
async function hasForeignUpdates(missionId: string) {
  if (!(await mainHasUpdates(missionId)))
    return false;

  const { items } = await client.operations.query
    .eventsList({ missionId, fromDate: await missionConfig.get(missionId, 'syncAt') })
    .then(thenData);
  const known = new Set(await idb.mutationsEvents.toCollection().primaryKeys());

  return items.some(item => !known.has(item.id));
}

// The server moves attachments while storing the event: they must be there before it
export async function uploadMissingFiles(
  events: MutationEvent[],
  hooks: { onFile?(index: number, total: number): void; onProgress?(percent: number): void } = {},
) {
  const files = getFilesToUpload(events);
  if (!files.length)
    return;

  const exists = await withTimeout(
    client.files.query.exists({ files: files.map(file => file.uri) }).then(thenData),
    SYNC_TIMEOUT,
  );
  const missing = files.filter(file => !exists[file.uri]);

  for (const [index, file] of missing.entries()) {
    hooks.onFile?.(index + 1, missing.length);

    const local = await idb.files.get(file.uri);
    if (!local)
      throw new Error(`File ${file.uri} is missing on this device`);

    await withTimeout(
      client.files.mutation.upload(
        { file: local.blob, path: file.uri, overwrite: true },
        { onUploadProgress: event => hooks.onProgress?.(Math.round((event.progress || 0) * 100)) },
      ),
      UPLOAD_TIMEOUT,
    );
  }
}

export async function localHasUpdates(missionId: string) {
  return (await getUnsyncedEvents(missionId)).length > 0;
}

export async function mainHasUpdates(missionId: string) {
  return client.operations.query
    .eventsHasUpdates({
      missionId,
      fromDate: await missionConfig.get(missionId, 'syncAt'),
    })
    .then(thenData);
}

export async function needSync(missionId: string) {
  if (await localHasUpdates(missionId))
    return true;
  return mainHasUpdates(missionId);
}

export async function syncEventFromMain(event: MutationEventWithLog) {
  // Already applied here (merge windows include the known history)
  if ((await idb.mutationsEvents.get(event.id))?.syncAt)
    return;

  await idb.mutationsEvents.put(event);
  switch (event.type) {
    case 'maintenance':
      await updateMaintenanceFromMain(event.log as MaintenanceLog);
      break;

    case 'move':
      const moveLog = event.log as MoveModuleLog;

      await idb.moves.put(moveLog);

      const lastMoveLog = await getLastMoveLog(moveLog.moduleSerialNumber);
      if (lastMoveLog.eventId === event.id) {
        const module = await idb.modules.get(moveLog.moduleSerialNumber!);
        if (!module)
          throw new Error('Module not found');

        module.funcLocationId = moveLog.to.funcLocationId;
        module.emplacementId = moveLog.to.emplacementId;
        await idb.modules.put(module);
      }
      break;

    case 'stock':
      const stockLog = event.log as StockLog;

      await idb.stocksLogs.put(stockLog);

      const lastStockLog = await getLastStockLog(stockLog.amosId, stockLog.emplacementId);
      if (lastStockLog.eventId === event.id) {
        const stock = await idb.stocks.get({
          amosId: stockLog.amosId,
          emplacementId: stockLog.emplacementId,
        });

        await idb.stocks.put({
          ...stock,
          storeId: stockLog.storeId,
          emplacementId: stockLog.emplacementId,
          amosId: stockLog.amosId,
          amosCode: stock?.amosCode,
          quantity: stockLog.quantityAfter,
        });
      }
      break;

    case 'counter':
      const counterLog = event.log as CounterLog;
      await idb.countersLogs.put(counterLog);
      break;

    case 'resetOverdue':
      const resetOverdueLog = event.log as ResetOverdueLog;
      await idb.resetOverdueLogs.put(resetOverdueLog);
      break;

    case 'machineStatus':
      const machineStatusLog = event.log as MachineStatusLog;
      await idb.machineStatusLogs.put(machineStatusLog);
      break;
  }
}

interface SocketIoMutationEvent {
  missionId: string;
  eventId: string;
}

export async function onMutationEvent({ missionId, eventId }: SocketIoMutationEvent) {
  if (await idb.missions.get(missionId)) {
    const localEvent = await idb.mutationsEvents.get(eventId);
    if (!localEvent?.syncAt) {
      // The cursor moves only over events that were all fetched: an event received alone could jump
      // over one missed while the socket was down, and that one would never be fetched
      const { items } = await client.operations.query
        .eventsList({ missionId, fromDate: await missionConfig.get(missionId, 'syncAt') })
        .then(thenData);
      for (const item of items)
        await syncEventFromMain(item);

      const lastSyncAt = items.reduce((max, item) => (item.syncAt! > max ? item.syncAt! : max), '');
      if (lastSyncAt)
        await updateSyncAt(missionId, lastSyncAt);

      // shortcut: a list cut by its page size may not hold the event, it is applied alone and the
      // cursor stays: the next full sync fetches it again (harmless, it is skipped when known)
      if (!items.some(item => item.id === eventId))
        await syncEventFromMain(await client.operations.query.eventWithLog({ id: eventId }).then(thenData));
    }
  }

  if (await localHasUpdates(missionId)) {
    missionContextState.hasEventsUpdates = true;
  }
}
socketIo.on('mutationEvent', onMutationEvent);
// Back online: send what was recorded while offline
socketIo.on('connect', () => void syncLocalToMain());
window.addEventListener('online', () => void syncLocalToMain());

export async function updateSyncAt(missionId: string, date: string) {
  const current = await missionConfig.get(missionId, 'syncAt');
  if (!current || isAfter(new Date(date), new Date(current))) {
    await missionConfig.set(missionId, 'syncAt', date);
  }
}

async function getLastStockLog(amosId: number, emplacementId: string): Promise<StockLog> {
  const logs = await idb.stocksLogs
    .where({
      amosId,
      emplacementId,
    })
    .reverse()
    .sortBy('logDate');

  return logs[0];
}

async function attachLog(event: MutationEventWithLog) {
  switch (event.type) {
    case 'maintenance':
      event.log = (await idb.maintenances.get({ eventId: event.id }))!;
      break;
    case 'move':
      event.log = (await idb.moves.get({ eventId: event.id }))!;
      break;
    case 'stock':
      event.log = (await idb.stocksLogs.get({ eventId: event.id }))!;
      break;
    case 'counter':
      event.log = (await idb.countersLogs.get({ eventId: event.id }))!;
      break;
    case 'resetOverdue':
      event.log = (await idb.resetOverdueLogs.get({ eventId: event.id }))!;
      break;
    case 'machineStatus':
      event.log = (await idb.machineStatusLogs.get({ eventId: event.id }))!;
      break;
  }
  return event;
}

// What already happened to the modules, func locations and machines the local events touch, however
// old: the rules compare an event with its neighbours, not only with what came after the last sync
async function relatedHistory(localEvents: MutationEvent[]) {
  const keys = <T>(pick: (event: MutationEvent) => T | null | undefined) =>
    [...new Set(localEvents.map(pick).filter((value): value is T => value !== undefined && value !== null))];

  const found = await Promise.all([
    idb.mutationsEvents.where('moduleSerialNumber').anyOf(keys(event => event.moduleSerialNumber)).toArray(),
    idb.mutationsEvents.where('funcLocationId').anyOf(keys(event => event.funcLocationId)).toArray(),
    idb.mutationsEvents
      .where('machineId')
      .anyOf(keys(event => event.machineId))
      .filter(event => event.type === 'machineStatus')
      .toArray(),
  ]);

  const history = new Map(found.flat().filter(event => !!event.syncAt).map(event => [event.id, event]));
  const withLogs = await Promise.all(
    [...history.values()].map(event => attachLog(event as MutationEventWithLog)),
  );
  return withLogs.filter(event => !!event.log);
}

export async function getEventsToMerge(missionId: string) {
  const localEvents = await Promise.all(
    ((await getUnsyncedEvents(missionId)) as MutationEventWithLog[]).map(attachLog),
  );

  const lastSync = (await missionConfig.get(missionId, 'syncAt'))!;
  const { items: mainEvents } = await client.operations.query
    .eventsList({
      missionId,
      fromDate: lastSync,
    })
    .then(thenData);

  // Already on the server (the echo was lost): the server version replaces the local one
  const mainIds = new Set(mainEvents.map(item => item.id));
  const history = (await relatedHistory(localEvents)).filter(item => !mainIds.has(item.id));

  return [
    ...localEvents.filter(item => !!item.log && !mainIds.has(item.id)),
    ...history,
    ...mainEvents,
  ] satisfies EventMergeRecord[];
}

export function getFilesToUpload(events: MutationEvent[]) {
  return (events.filter(event => event.type === 'maintenance') as MutationEventMaintenance[])
    .map(event => [...event.payload.pictures || [], ...event.payload.documents || []])
    .flat();
}

async function updateMaintenanceFromMain(log: MaintenanceLog) {
  const files = [...log.pictures, ...log.documents].map(item => item.uri);

  if (files.length) {
    const localLog = await idb.maintenances.get(log.eventId);

    if (localLog) {
      const localFiles = [...localLog.pictures, ...localLog.documents].map(item => item.uri);

      // If local have files, update their paths
      for (const filePath of files) {
        const localFile = localFiles.find(
          localFilePath => basename(filePath) === basename(localFilePath),
        );
        if (localFile) {
          await idb.files.update(localFile, {
            path: filePath,
          });
        }
      }
    }
    else {
      // Else add files to download queue
      downloaderAddFiles(...files);
    }
  }

  await idb.maintenances.put(log);
}
